import { useEffect, useState, type ReactElement } from 'react';
import { v2, ApiError, parseApiErrorBody, type SpecFile } from '@/api/v2Client';
import { MovableDialog } from '@/components/shared/MovableDialog';

/**
 * phase-spec-files-management — in-app view of a spec file's contents.
 *
 * These are all plain-text formats (DTD/XSD/XSL/CSS); legibility is the
 * requirement. Renders the text in a scrollable monospace block with the
 * file identity and hash, an honest source note when the bytes come from
 * the runtime-embedded ICH DTD (the copy actually in force), and an equally
 * honest empty state when the row is a metadata-only reference with no
 * stored content.
 */

export interface SpecFileContentModalProps {
  readonly specFile: SpecFile;
  readonly onClose: () => void;
}

interface ContentData {
  content: string;
  source: 'stored' | 'embedded';
  fileName: string | null;
  sha256: string | null;
  title: string;
  fileType: string;
}

export function SpecFileContentModal({
  specFile,
  onClose,
}: SpecFileContentModalProps): ReactElement {
  const [data, setData] = useState<ContentData | null>(null);
  const [noContent, setNoContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedId, setSelectedId] = useState(specFile.Id);
  const [companions, setCompanions] = useState<SpecFile[] | null>(null);
  const [companionsError, setCompanionsError] = useState<string | null>(null);
  const selectedFile = companions?.find((file) => file.Id === selectedId) ?? specFile;

  useEffect(() => {
    let cancelled = false;
    setSelectedId(specFile.Id);
    setCompanions(null);
    setCompanionsError(null);
    if ((specFile.CompanionCount ?? 0) > 0) {
      v2.specFiles.get(specFile.Id).then((detail) => {
        if (!cancelled) {
          if (detail.Companions) setCompanions(detail.Companions);
          else setCompanionsError('The server did not return the companion file list.');
        }
      }).catch(() => {
        if (!cancelled) setCompanionsError('The companion file list could not be loaded. Close and reopen this view to retry.');
      });
    }
    return () => { cancelled = true; };
  }, [specFile.Id, specFile.CompanionCount]);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setNoContent(null);
    setError(null);
    v2.specFiles
      .content(selectedId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const body = parseApiErrorBody(err);
        if (err instanceof ApiError && body?.error === 'NoContentStored') {
          setNoContent(body.message ?? 'No file content is stored for this specification file.');
        } else {
          setError(body?.message ?? (err instanceof Error ? err.message : String(err)));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, reloadKey]);

  const download = (): void => {
    if (!data) return;
    const blob = new Blob([data.content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = data.fileName ?? `${selectedFile.Title}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <MovableDialog
      persistKey="specFiles.content"
      testId="spec-file-content-modal"
      onClose={onClose}
      closeOnBackdrop
      resizable
      width="min(960px, 94vw)"
      minSize={{ width: 320, height: 360 }}
      ariaLabel={`Contents of ${specFile.Title}`}
      title={
        <span>
          {specFile.Title}
          <span style={{ marginLeft: 8, fontFamily: 'monospace', fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>
            {specFile.FileName ?? ''}
          </span>
        </span>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '70vh' }}>
        {(specFile.CompanionCount ?? 0) > 0 && (
          <div style={{ fontSize: 12 }}>
            {companions === null && companionsError === null && <div role="status">Loading companion files…</div>}
            {companionsError && <div role="alert">{companionsError}</div>}
            {companions && (
              <label>File in this set{' '}
                <select aria-label="File in specification set" value={selectedId} onChange={(event) => setSelectedId(event.target.value)} style={{ maxWidth: '100%', padding: 6 }}>
                  <option value={specFile.Id}>{specFile.FileName ?? specFile.Title} (primary)</option>
                  {companions.map((file) => <option key={file.Id} value={file.Id}>{file.FileName ?? file.Title}</option>)}
                </select>
              </label>
            )}
          </div>
        )}
        {selectedId !== specFile.Id && <div style={{ fontSize: 12 }}>{selectedFile.FileName ?? selectedFile.Title}</div>}
        {data === null && noContent === null && error === null && <div role="status">Loading file contents…</div>}
        {data?.source === 'embedded' && (
          <div
            data-testid="spec-file-content-embedded-note"
            style={{ fontSize: 12, color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6, padding: '6px 10px' }}
          >
            Showing the runtime-embedded copy — these are the exact bytes the compiler
            validates against (single source; SHA-256 pinned by test).
          </div>
        )}

        {noContent !== null && (
          <div
            data-testid="spec-file-no-content"
            style={{ padding: '24px 12px', fontSize: 13, color: 'var(--text-muted)', border: '1px dashed var(--border)', borderRadius: 6, textAlign: 'center' }}
          >
            {noContent}
          </div>
        )}
        {error !== null && (
          <div role="alert" style={{ color: 'var(--red)', fontSize: 13, padding: 12 }}>
            {error}
            <button type="button" onClick={() => setReloadKey((key) => key + 1)} style={{ marginLeft: 12 }}>Retry contents</button>
          </div>
        )}
        {data !== null && (
          <>
            <pre
              data-testid="spec-file-content-pre"
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: 12,
                fontSize: 12,
                lineHeight: 1.5,
                whiteSpace: 'pre',
                margin: 0,
              }}
            >
              {data.content}
            </pre>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {data.sha256 && (
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>
                  sha256: {data.sha256.slice(0, 16)}…
                </span>
              )}
              <button
                type="button"
                data-testid="spec-file-download-btn"
                onClick={download}
                style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 6, border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Download
              </button>
            </div>
          </>
        )}
      </div>
    </MovableDialog>
  );
}
